import * as React from "react";
import { AlertCircle } from "lucide-react";
import { Pill, type PillTone } from "@/components/ui/pill";
import { useOee } from "@/features/oee/hooks";
import { useActiveRun } from "@/features/production/hooks";
import { derivePlanAdherenceVerdict } from "@/features/oee/plan-adherence-verdict";
import { usePlanAdherenceBounds } from "@/hooks/useOrganizationPolicy";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { LineSummary } from "@/types/lines.types";

const CONF: Record<string, { label: string; tone: PillTone }> = {
  HIGH: { label: "alta", tone: "success" },
  MEDIUM: { label: "média", tone: "warning" },
  LOW: { label: "baixa", tone: "danger" },
  INSUFFICIENT: { label: "insuf.", tone: "neutral" },
};

const LABEL = "text-[10px] font-medium uppercase tracking-[0.08em] text-text-secondary";
const VALUE = "font-mono text-[18px] font-medium leading-none text-text-primary tracking-[-0.01em]";

function pct(v: number | null | undefined): string {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

// Indicador compacto p/ células de KPI densas onde um <Notify.QueryError/> completo
// não cabe; nunca deixa a falha de fetch virar "—"/"Sem plano" (que pareceriam dado real).
function InlineMetricError({ message }: { message: string }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1 text-[12px] text-rd-danger-700" title={message}>
      <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
      Erro
    </span>
  );
}

export function LineCardMetrics({ line }: { line: LineSummary }): React.ReactElement {
  const {
    data: oee,
    isError: oeeIsError,
    error: oeeError,
  } = useOee(line.id, "24h", {
    breakdown: true,
  });
  const { data: run } = useActiveRun(line.id);

  // Só mostra o pill de erro quando não há dado válido (mesmo desatualizado) pra
  // exibir: TanStack Query não limpa `data` num refetch de fundo que falha, então tratar
  // `oeeIsError` sozinho trocaria um número correto (só desatualizado) por "Erro" a cada
  // hiccup transitório de rede/WS (useOee reagenda a cada 60s + em todo OEE_UPDATED).
  const oeeShowError = oeeIsError && !oee;
  const oeeErrorMessage = oeeShowError ? getFriendlyErrorMessage(oeeError) : null;

  const conf = oee ? (CONF[oee.confidenceLevel] ?? CONF.INSUFFICIENT) : null;
  const adh = oee?.planAdherence ?? null;
  // Spec 079 — as faixas do veredito vêm da organização, não de constante de módulo.
  const bounds = usePlanAdherenceBounds();
  const verdict =
    adh && adh.value !== null && adh.basis !== null
      ? derivePlanAdherenceVerdict(adh.basis, adh.value, adh.overrunPct, bounds)
      : null;
  const adhMissingPlan = adh != null && !verdict;

  const product = run?.products?.[0]?.product.name ?? null;
  const progress =
    run && run.plannedQty > 0 ? Math.min(100, (run.producedQty / run.plannedQty) * 100) : 0;

  return (
    <>
      <div className="min-w-0 space-y-1">
        <div className={LABEL}>OEE 24h</div>
        {oeeShowError && oeeErrorMessage ? (
          <InlineMetricError message={oeeErrorMessage} />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className={VALUE}>{pct(oee?.oee)}</span>
              {conf ? (
                <Pill tone={conf.tone} withDot>
                  conf. {conf.label}
                </Pill>
              ) : null}
            </div>
            <div className="mt-1 flex gap-3 text-[11px] text-text-secondary">
              <span>
                A <b className="font-mono text-text-primary">{pct(oee?.availability)}</b>
              </span>
              <span>
                P <b className="font-mono text-text-primary">{pct(oee?.performance)}</b>
              </span>
              <span>
                Q <b className="font-mono text-text-primary">{pct(oee?.quality)}</b>
              </span>
            </div>
          </>
        )}
      </div>

      <div className="min-w-0 space-y-1">
        <div className={LABEL}>Aderência ao plano</div>
        <div className="flex items-center gap-2">
          {oeeShowError && oeeErrorMessage ? (
            <InlineMetricError message={oeeErrorMessage} />
          ) : adhMissingPlan ? (
            <Pill tone="neutral" withDot>
              Sem plano
            </Pill>
          ) : (
            <>
              <span className={VALUE}>{adh?.value != null ? `${adh.value.toFixed(1)}%` : "—"}</span>
              {verdict ? (
                <Pill tone={verdict.tone} withDot>
                  {verdict.label}
                </Pill>
              ) : null}
            </>
          )}
        </div>
        <div className="mt-1 text-[11px] text-text-tertiary">
          {line.machineCount} máq.
          {line.availabilityEstimate7d != null ? (
            <>
              {" · disp 7d "}
              <span className="font-mono">
                {`${(line.availabilityEstimate7d * 100).toFixed(0)}%`}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="min-w-0 space-y-1">
        <div className={LABEL}>Ordem atual</div>
        {run && product ? (
          <>
            {}
            <div className="truncate text-[13px] font-medium text-text-primary">{product}</div>
            {}
            <div className="font-mono text-[11px] text-text-secondary">
              {run.producedQty.toLocaleString("pt-BR")} / {run.plannedQty.toLocaleString("pt-BR")}{" "}
              un · {progress.toFixed(0)}%
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg-muted-rd">
              <div
                className="h-full w-[var(--w)] rounded-full bg-brand-600"
                style={{ "--w": `${progress}%` } as React.CSSProperties}
              />
            </div>
          </>
        ) : (
          <div className="text-[11px] text-text-secondary">sem ordem ativa</div>
        )}
      </div>
    </>
  );
}
